import { Howl } from 'howler';
/**
 * 默认音频资源映射表（根据你的实际路径调整 require 路径）
 */
const SOUNDS_LIST = {
  'chicken-road-afk': [require('./assets/audio/chicken-road_afk.mp3')],
  'chicken-road-ambient': [require('./assets/audio/chicken-road-ambient.mp3')],
  'chicken-road-train':[ require('./assets/audio/chicken-road-train.mp3')],
  
  // 组件中调用的其他音效占位
  'chicken-road-jump-1': [require('./assets/audio/chicken-road-jump_1.mp3')],
  'chicken-road-jump-2':[ require('./assets/audio/chicken-road-jump_2.mp3')],
  'chicken-road-jump-3': [require('./assets/audio/chicken-road-jump_3.mp3')],
  'chicken-road-jump-4':[ require('./assets/audio/chicken-road-jump_4.mp3')],
  'chicken-road-win':[ require('./assets/audio/chicken-road-win.mp3')],
  'chicken-road-lose': [require('./assets/audio/chicken-road-lose.mp3')]
};

export default class SoundsService {
  constructor(options = {}) {
    // 配置项与 Vuex / State 适配
    
    // 允许传入返回 isMuted() 和 volume() 的函数（可结合 Vuex 或 Vue.observable）
    this.soundsState = options.soundsState || {
      isMuted: () => false,
      volume: () => 1
    };

    this.isBrowser = typeof window !== 'undefined';
    this.isIOS =
      this.isBrowser && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    this.isSafari =
      this.isBrowser &&
      /Safari/.test(navigator.userAgent) &&
      !/Chrome/.test(navigator.userAgent) &&
      !/Edg/.test(navigator.userAgent);

    this.soundCache = new Map();
    this.playingIds = new Map();
    this.soundThrottles = new Map();
    this.throttleInterval = {
      'wheel-spinner-click': 90,
      'jackpot-spinner-click': 90,
      'cases-step': 150,
      default: 50
    };

    this.maxActiveSounds = this.isIOS ? 3 : 8;
    this.activeSoundsCount = 0;
    this.poolExhaustionCount = 0;
    this.lastPoolExhaustionTime = 0;
    this.audioUnlocked = false;
    this.cleanupIntervalId = null;

    if (this.isIOS || this.isSafari) {
      this.preloadCommonSounds();
      this.setupAudioUnlock();
    }

    if (this.isBrowser) {
      this.cleanupIntervalId = setInterval(() => this.cleanupUnusedSounds(), 30000);
    }
  }

  /** 销毁服务（可在根组件 destroy 时调用） */
  destroy() {
    if (this.cleanupIntervalId !== null) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }
    this.stopAllSounds();
  }

  /** 播放声音（返回 Howl 实例，方便外部控制） */
  playSound(id, volume, loop) {
    if (!this.isBrowser || this.soundsState.isMuted()) return null;
    if (this.shouldThrottleSound(id)) return null;

    try {
      const sound = this.getSoundInstance(id, volume, !!loop);
      if (!sound) return null;

      // Safari/iOS 触发解锁兼容逻辑
      if ((this.isIOS || this.isSafari) && !this.audioUnlocked) {
        const playPromise = sound.play();
        if (playPromise !== undefined && typeof playPromise === 'number') {
          return sound;
        }

        setTimeout(() => {
          if (this.audioUnlocked && !sound.playing()) {
            sound.play();
          }
        }, 50);

        return sound;
      }

      sound.play();
      return sound;
    } catch (error) {
      const now = Date.now();
      if (now - this.lastPoolExhaustionTime > 1000) {
        this.poolExhaustionCount++;
        this.lastPoolExhaustionTime = now;
      }
      return null;
    }
  }

  /** 停止指定音效 */
  stop(id, loop = false) {
    const cacheKey = this.cacheKey(id, loop);
    const cache = this.soundCache.get(cacheKey);
    if (cache) cache.instance.stop();
  }

  /** 获取已缓存的 Howl 实例 */
  getCached(id, loop = false) {
    const cache = this.soundCache.get(this.cacheKey(id, loop));
    return cache?.instance || null;
  }

  /** 全局停止所有音效 */
  stopAllSounds() {
    for (const [, cache] of this.soundCache.entries()) {
      cache.instance.stop();
    }
    this.playingIds.clear();
    this.activeSoundsCount = 0;
  }

  // === 内部私有方法 ===

  cacheKey(id, loop) {
    return `${id}-${loop ? 'loop' : 'once'}`;
  }

  totalActive() {
    let n = 0;
    for (const s of this.playingIds.values()) n += s.size;
    return n;
  }

  getSoundInstance(id, volume, loop) {
    const key = this.cacheKey(id, !!loop);
    const now = Date.now();
    const userVolume = this.soundsState.volume();
    const normalizedVolume = typeof volume === 'number' ? Math.max(0, Math.min(1, volume)) : undefined;
    const targetVolume = typeof normalizedVolume === 'number'
      ? Math.min(normalizedVolume, userVolume)
      : userVolume;

    const cached = this.soundCache.get(key);
    if (cached) {
      cached.lastPlayed = now;
      cached.instance.volume(targetVolume);
      return cached.instance;
    }

    if (this.activeSoundsCount >= this.maxActiveSounds) {
      this.removeOldestSound();
    }

    const fileNames = SOUNDS_LIST[id];
    if (!fileNames || !Array.isArray(fileNames) || fileNames.length === 0) return null;

    const src = fileNames;
    const useHtml5 = this.isIOS || this.isSafari;
    const poolSize = useHtml5 ? 2 : 10;

    const sound = new Howl({
      src,
      volume: targetVolume,
      loop: !!loop,
      html5: useHtml5,
      preload: true,
      pool: poolSize,
      format: fileNames.map(f => f.split('.').pop() || 'mp3'),
      onloaderror: () => {},
      onplayerror: () => {
        sound.once('unlock', () => sound.play());
      }
    });

    if (!this.playingIds.has(key)) this.playingIds.set(key, new Set());
    const set = this.playingIds.get(key);

    sound.on('play', (soundId) => {
      set.add(soundId);
      this.activeSoundsCount = this.totalActive();
    });

    sound.on('end', (soundId) => {
      set.delete(soundId);
      this.activeSoundsCount = this.totalActive();
    });

    sound.on('stop', (soundId) => {
      if (typeof soundId === 'number') set.delete(soundId);
      else set.clear();
      this.activeSoundsCount = this.totalActive();
    });

    this.soundCache.set(key, { instance: sound, lastPlayed: now });

    return sound;
  }

  removeOldestSound() {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, cache] of this.soundCache.entries()) {
      const playing = cache.instance.playing();
      if (!playing && cache.lastPlayed < oldestTime) {
        oldestTime = cache.lastPlayed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      const cache = this.soundCache.get(oldestKey);
      cache.instance.off();
      cache.instance.unload();
      this.soundCache.delete(oldestKey);
      this.playingIds.delete(oldestKey);
      this.activeSoundsCount = this.totalActive();
      return;
    }

    let candidateKey = null;
    oldestTime = Infinity;

    for (const [key, cache] of this.soundCache.entries()) {
      const isLoop = key.endsWith('-loop');
      if (!isLoop && cache.lastPlayed < oldestTime) {
        oldestTime = cache.lastPlayed;
        candidateKey = key;
      }
    }

    if (candidateKey) {
      const cache = this.soundCache.get(candidateKey);
      cache.instance.stop();
    }
  }

  preloadCommonSounds() {
    const common = ['wheel-spinner-click', 'jackpot-spinner-click', 'success-notify'];
    common.forEach((id) => this.getSoundInstance(id, 0, false));
  }

  setupAudioUnlock() {
    if (this.audioUnlocked) return;

    const unlock = () => {
      if (this.audioUnlocked) return;

      const silentSound = new Howl({
        src: ['data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'],
        volume: 0,
        html5: true,
        preload: true
      });

      silentSound.once('play', () => {
        this.audioUnlocked = true;
        silentSound.unload();
        this.preloadCaseSounds();

        document.removeEventListener('touchstart', unlock);
        document.removeEventListener('touchend', unlock);
        document.removeEventListener('click', unlock);
      });

      silentSound.play();
    };

    document.addEventListener('touchstart', unlock, { once: true, passive: true });
    document.addEventListener('touchend', unlock, { once: true, passive: true });
    document.addEventListener('click', unlock, { once: true, passive: true });
  }

  preloadCaseSounds() {
    if (!this.audioUnlocked) return;

    const caseSounds = [
      'cases-open-process',
      'cases-open-process-fast',
      'cases-step',
      'cases-base-drop',
      'cases-fast-drop',
      'cases-base-topdrop',
      'cases-fast-topdrop'
    ];

    caseSounds.forEach((id) => this.getSoundInstance(id, 0, false));
  }

  shouldThrottleSound(id) {
    const now = Date.now();
    const key = String(id);
    const interval =
      this.throttleInterval[key] ||
      (key.includes('-click') || key.includes('spinner') ? this.throttleInterval['default'] : 0);

    if (!interval) return false;

    const last = this.soundThrottles.get(key) || 0;
    if (now - last < interval) return true;

    this.soundThrottles.set(key, now);
    return false;
  }

  cleanupUnusedSounds() {
    const now = Date.now();
    const maxAge = 30000;

    for (const [key, cache] of this.soundCache.entries()) {
      if (cache.instance.playing()) continue;
      if (now - cache.lastPlayed < maxAge) continue;

      try {
        cache.instance.off();
        cache.instance.unload();
        this.soundCache.delete(key);
        this.playingIds.delete(key);
      } catch (error) {
        this.soundCache.delete(key);
        this.playingIds.delete(key);
      }
    }
    this.activeSoundsCount = this.totalActive();
  }
}